import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { api, paymentsAPI, SubscriptionInfo } from '../../services/api';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  monthlyRateCents?: number | null;
}

interface Workout {
  id: string;
  name: string;
  description?: string;
  status: string;
  scheduledDate?: string;
  completedDate?: string;
  estimatedDuration?: number;
  createdAt: string;
}

interface MealPlan {
  id: string;
  name: string;
  description?: string;
  status: string;
  startDate: string;
  endDate: string;
  mealCount: number;
  createdAt: string;
}

interface ProgressEntry {
  id: string;
  type: string;
  value: number;
  unit: string;
  notes?: string;
  recordedAt: string;
}

type AccordionType = 'billing' | 'workouts' | 'meals' | 'progress' | null;

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [client, setClient] = useState<Client | null>(null);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
  const [progress, setProgress] = useState<ProgressEntry[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [monthlyRateInput, setMonthlyRateInput] = useState('');
  const [isSavingRate, setIsSavingRate] = useState(false);
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<AccordionType>('billing');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id || !user?.roles || user.roles.indexOf('TRAINER') === -1) return;

    const fetchClientData = async () => {
      try {
        setIsLoading(true);

        const clientResponse = await api.get(`/clients/${id}`);
        setClient(clientResponse.data);
        setMonthlyRateInput(
          clientResponse.data.monthlyRateCents
            ? (clientResponse.data.monthlyRateCents / 100).toFixed(2)
            : ''
        );

        const billingResponse = await paymentsAPI.getSubscription(id);
        setSubscription(billingResponse.data.subscription);

        const workoutsResponse = await api.get(`/workouts/client/${id}`);
        setWorkouts(workoutsResponse.data.workouts || []);

        try {
          const mealsResponse = await api.get(`/meals/client/${id}`);
          setMealPlans(mealsResponse.data.mealPlans || []);
        } catch {
          setMealPlans([]);
        }

        setProgress([]);
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [id, user]);

  if (!user?.roles || user.roles.indexOf('TRAINER') === -1) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Access denied. Trainer access required.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Client not found</p>
      </div>
    );
  }

  const toggleAccordion = (accordion: AccordionType) => {
    setOpenAccordion(openAccordion === accordion ? null : accordion);
  };

  const formatDate = (dateString: string) => {
    let date: Date;
    if (dateString.includes('T')) {
      const datePart = dateString.split('T')[0];
      date = new Date(datePart + 'T00:00:00');
    } else {
      date = new Date(dateString + 'T00:00:00');
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (value?: string) => {
    if (!value) return null;
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const cancellationLabel = (sub: SubscriptionInfo) => {
    const who =
      sub.canceledByRole === 'TRAINER'
        ? 'trainer'
        : sub.canceledByRole === 'CLIENT'
          ? 'client'
          : 'system';
    const when = formatDateTime(sub.canceledAt);
    return when ? `Canceled by ${who} on ${when}` : `Canceled by ${who}`;
  };

  const saveMonthlyRate = async () => {
    if (!id) return;
    const dollars = Number(monthlyRateInput);
    if (!Number.isFinite(dollars) || dollars < 5) {
      toast.error('Enter a monthly rate of at least $5.00');
      return;
    }

    setIsSavingRate(true);
    try {
      const monthlyRateCents = Math.round(dollars * 100);
      const response = await paymentsAPI.setClientRate(id, monthlyRateCents);
      setClient((current) =>
        current ? { ...current, monthlyRateCents: response.data.monthlyRateCents } : current
      );
      toast.success('Monthly rate saved');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save monthly rate');
    } finally {
      setIsSavingRate(false);
    }
  };

  const runSubscriptionAction = async (action: 'pause' | 'resume' | 'cancel') => {
    if (!id) return;
    if (action === 'cancel' && !window.confirm('Cancel this subscription immediately with no refund?')) {
      return;
    }

    setIsUpdatingSubscription(true);
    try {
      const response =
        action === 'pause'
          ? await paymentsAPI.pauseSubscription(id)
          : action === 'resume'
            ? await paymentsAPI.resumeSubscription(id)
            : await paymentsAPI.cancelSubscription(id);
      setSubscription(response.data.subscription);
      toast.success(
        action === 'pause'
          ? 'Subscription paused'
          : action === 'resume'
            ? 'Subscription resumed'
            : 'Subscription canceled'
      );
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Failed to ${action} subscription`);
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/trainer/clients')}
              className="p-1 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {client.firstName} {client.lastName}
              </h1>
              <p className="text-sm text-gray-600">@{client.username}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('billing')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-base font-medium text-gray-900">Billing</h3>
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm capitalize">
                  {subscription?.status || 'not subscribed'}
                </span>
              </div>
              {openAccordion === 'billing' ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {openAccordion === 'billing' && (
            <div className="border-t border-gray-200 p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly rate (USD)
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">$</span>
                    <input
                      type="number"
                      min="5"
                      step="0.01"
                      value={monthlyRateInput}
                      onChange={(event) => setMonthlyRateInput(event.target.value)}
                      className="w-full rounded-md border border-gray-300 py-2 pl-7 pr-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="99.00"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={saveMonthlyRate}
                    disabled={isSavingRate}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isSavingRate ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  WebFit keeps 20%. Stripe fees come out of WebFit&apos;s share.
                </p>
              </div>

              {subscription ? (
                <div className="space-y-3 rounded-md bg-gray-50 p-3">
                  <p className="text-sm text-gray-700">
                    Status: <span className="font-medium capitalize">{subscription.status}</span>
                  </p>
                  <p className="text-sm text-gray-700">
                    Amount: ${(subscription.monthlyAmountCents / 100).toFixed(2)} / month
                  </p>
                  {subscription.status === 'canceled' && (
                    <p className="text-sm font-medium text-red-700">
                      {cancellationLabel(subscription)}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {subscription.status === 'active' && (
                      <button
                        type="button"
                        disabled={isUpdatingSubscription}
                        onClick={() => runSubscriptionAction('pause')}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
                      >
                        Pause
                      </button>
                    )}
                    {subscription.status === 'paused' && (
                      <button
                        type="button"
                        disabled={isUpdatingSubscription}
                        onClick={() => runSubscriptionAction('resume')}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
                      >
                        Resume
                      </button>
                    )}
                    {subscription.status !== 'canceled' && (
                      <button
                        type="button"
                        disabled={isUpdatingSubscription}
                        onClick={() => runSubscriptionAction('cancel')}
                        className="rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancel now
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  No active subscription yet. After you save a rate, the client can subscribe from their dashboard.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('workouts')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-base font-medium text-gray-900">Workouts</h3>
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm">
                  {workouts.length}
                </span>
              </div>
              {openAccordion === 'workouts' ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {openAccordion === 'workouts' && (
            <div className="border-t border-gray-200">
              <div className="p-3 border-b border-gray-100">
                <button
                  onClick={() => navigate(`/trainer/workout-builder?clientId=${id}`)}
                  className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Add New Workout</span>
                </button>
              </div>

              {workouts.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No workouts yet. Create the first one!
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {workouts
                    .sort((a, b) => {
                      const aDate = a.scheduledDate || a.createdAt;
                      const bDate = b.scheduledDate || b.createdAt;
                      return new Date(bDate).getTime() - new Date(aDate).getTime();
                    })
                    .map((workout) => (
                      <div
                        key={workout.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/trainer/workout-builder?clientId=${id}&workoutId=${workout.id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">{workout.name}</h4>
                          <span className="text-xs text-gray-500">
                            {workout.status === 'COMPLETED' && workout.completedDate
                              ? formatDate(workout.completedDate)
                              : workout.scheduledDate ? formatDate(workout.scheduledDate) : formatDate(workout.createdAt)
                            }
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('meals')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-base font-medium text-gray-900">Meal Plans</h3>
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm">
                  {mealPlans.length}
                </span>
              </div>
              {openAccordion === 'meals' ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {openAccordion === 'meals' && (
            <div className="border-t border-gray-200">
              <div className="p-3 border-b border-gray-100">
                <button
                  onClick={() => navigate(`/trainer/meal-builder?clientId=${id}`)}
                  className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-500 hover:text-green-600 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Add New Meal Plan</span>
                </button>
              </div>

              {mealPlans.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No meal plans yet. Create the first one!
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {mealPlans
                    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                    .map((plan) => (
                      <div
                        key={plan.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/trainer/meal-builder?clientId=${id}&mealPlanId=${plan.id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">{plan.name}</h4>
                            <p className="text-xs text-gray-500">{plan.mealCount} meals</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              plan.status === 'COMPLETED'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {plan.status}
                            </span>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {formatDate(plan.startDate)} – {formatDate(plan.endDate)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('progress')}
            className="w-full px-4 py-4 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-medium text-gray-900">Progress</h3>
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm">
                  {progress.length}
                </span>
              </div>
              {openAccordion === 'progress' ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {openAccordion === 'progress' && (
            <div className="border-t border-gray-200">
              <div className="p-4 border-b border-gray-100">
                <button
                  onClick={() => {/* TODO: Navigate to progress entry */}}
                  className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-500 hover:text-purple-600 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Add Progress Entry</span>
                </button>
              </div>

              <div className="p-4 text-center text-gray-500">
                Progress tracking coming soon!
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
